import { createClient, AuthChangeEvent, Session, RealtimeChannel } from '@supabase/supabase-js';
import { Issue, User, UserRole, IssueStatus, IssuePriority } from '../types';

// Supabase Configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Storage bucket name — create this in Supabase Dashboard → Storage
const ATTACHMENTS_BUCKET = 'attachments';

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ---------------------------------------------------------------------------
// Column lists
// ---------------------------------------------------------------------------

// Lightweight columns for list views — excludes heavy JSON columns
const ISSUE_LIST_COLUMNS = [
  'id', 'title', 'status', 'priority', 'category', 'sub_category',
  'place', 'location', 'reported_by', 'reported_by_name',
  'assigned_to', 'assigned_to_name', 'created_at', 'updated_at'
].join(', ');

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

const mapIssue = (row: any): Issue => ({
  id: row.id,
  title: row.title,
  description: row.description ?? '',
  status: (row.status?.toUpperCase() as IssueStatus) || IssueStatus.OPEN,
  priority: (row.priority?.toUpperCase() as IssuePriority) || IssuePriority.MEDIUM,
  category: row.category ?? '',
  subCategory: row.sub_category,
  place: row.place || 'Outlet',
  reportedBy: row.reported_by,
  reportedByName: row.reported_by_name || 'Unknown',
  assignedTo: row.assigned_to,
  assignedToName: row.assigned_to_name,
  createdAt: new Date(row.created_at).getTime(),
  updatedAt: new Date(row.updated_at).getTime(),
  comments: row.comments || [],
  attachments: row.attachments || [],
  location: row.location,
  aiAnalysis: row.ai_analysis
});

const mapProfile = (row: any): User => ({
  id: row.id,
  email: row.email,
  name: row.full_name || 'User',
  role: (row.role?.toUpperCase() as UserRole) || UserRole.STAFF,
  avatar: row.avatar_url,
  assignedOutlets: row.assigned_outlets || [],
  viewOnlyAssigned: row.view_only_assigned || false
});

// ---------------------------------------------------------------------------
// Error handler
// ---------------------------------------------------------------------------

const handleSupabaseError = (error: any, context: string) => {
  console.error(`Error in ${context}:`, error);

  // Handle specific database error codes
  if (error?.code === 'PGRST205') {
    throw new Error("Database setup incomplete. The 'issues' table is missing. Please run the SQL setup script.");
  }
  if (error?.code === '42P17') {
    throw new Error("Database configuration error. Infinite recursion in policies detected. Please run the SQL fix script.");
  }
  if (error?.code === '23503' || (error?.message && error.message.includes('foreign key constraint'))) {
    throw new Error("Cannot delete this user because they have reported issues or comments. Please reassign their issues first or run the 'Enhanced Delete' SQL script.");
  }
  if (error?.code === 'P0001') {
    // Custom exception raised in SQL
    throw new Error(error.message || "Database execution error.");
  }
  
  if (error?.message) {
    throw new Error(error.message);
  }
  throw new Error(`An unexpected database error occurred (${error?.code || 'unknown'}).`);
};

// ---------------------------------------------------------------------------
// StorageService
// ---------------------------------------------------------------------------

export const StorageService = {

  // -------------------------------------------------------------------------
  // Auth
  // -------------------------------------------------------------------------

  onAuthChange: (callback: (event: AuthChangeEvent, session: Session | null) => void) => {
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
    return data.subscription;
  },

  signUp: async (email: string, password: string, name: string, role: UserRole) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          role: role,
          avatar_url: `https://ui-avatars.com/api/?name=${name}&background=random`
        }
      }
    });
    if (error) handleSupabaseError(error, 'signUp');
    return data;
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) handleSupabaseError(error, 'signIn');
    return data;
  },

  signOut: async () => {
    await supabase.auth.signOut();
  },

  getCurrentUser: async (): Promise<User | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle();

    if (error) {
       console.warn("Profile fetch warning (falling back to metadata):", error);
    }

    if (profile) return mapProfile(profile);

    const metadata = session.user.user_metadata;
    return {
      id: session.user.id,
      email: session.user.email || '',
      name: metadata.full_name || 'User',
      role: (metadata.role?.toUpperCase() as UserRole) || UserRole.STAFF,
      avatar: metadata.avatar_url,
      assignedOutlets: metadata.assigned_outlets || [],
      viewOnlyAssigned: metadata.view_only_assigned || false
    };
  },

  updateProfile: async (userId: string, updates: { name?: string; avatar?: string }) => {
    const payload: any = {};
    if (updates.name) payload.full_name = updates.name;
    if (updates.avatar) payload.avatar_url = updates.avatar;
    payload.updated_at = new Date().toISOString();

    const { error } = await supabase.from('profiles').update(payload).eq('id', userId);
    if (error) handleSupabaseError(error, 'updateProfile');

    if (updates.name || updates.avatar) {
      await supabase.auth.updateUser({
        data: { full_name: updates.name, avatar_url: updates.avatar }
      });
    }
  },

  updatePassword: async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) handleSupabaseError(error, 'updatePassword');
  },

  // -------------------------------------------------------------------------
  // User Management (Admin)
  // -------------------------------------------------------------------------

  getUsers: async (): Promise<User[]> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('full_name', { ascending: true });

    if (error) {
      handleSupabaseError(error, 'getUsers');
      return [];
    }
    return data.map(mapProfile);
  },
  createUserAsAdmin: async (email: string, password: string, name: string, role: UserRole, assignedOutlets?: string[], viewOnlyAssigned?: boolean) => {
    const tempClient = createClient(SUPABASE_URL, SUPABASE_KEY, {
      auth: { persistSession: false }
    });

    const { data, error } = await tempClient.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          role: role,
          avatar_url: `https://ui-avatars.com/api/?name=${name}&background=random`,
          assigned_outlets: (role === UserRole.MANAGER || role === UserRole.STAFF) ? (assignedOutlets || []) : []
        }
      }
    });

    if (error) handleSupabaseError(error, 'createUserAsAdmin');

    if (data?.user) {
      const profilePayload: any = { updated_at: new Date().toISOString() };
      let hasUpdates = false;

      if ((role === UserRole.MANAGER || role === UserRole.STAFF) && assignedOutlets?.length) {
        profilePayload.assigned_outlets = assignedOutlets;
        hasUpdates = true;
      }
      
      if (viewOnlyAssigned) {
        profilePayload.view_only_assigned = true;
        hasUpdates = true;
      }

      if (hasUpdates) {
        let saved = false;
        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 800));
          const { error: profileError } = await supabase
            .from('profiles')
            .update(profilePayload)
            .eq('id', data.user.id);
          if (!profileError) { saved = true; break; }
        }
        if (!saved) {
          console.warn('Could not persist additional profile data — profile row may not exist yet.');
        }
      }
    }

    return data;
  },

  updateUserRole: async (userId: string, newRole: UserRole) => {
    const { error } = await supabase
      .from('profiles')
      .update({ role: newRole, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) handleSupabaseError(error, 'updateUserRole');
  },

  updateUserOutlets: async (userId: string, outlets: string[]) => {
    const { error } = await supabase
      .from('profiles')
      .update({ assigned_outlets: outlets, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) handleSupabaseError(error, 'updateUserOutlets');
  },

  updateUserViewRestriction: async (userId: string, viewOnlyAssigned: boolean) => {
    const { error } = await supabase
      .from('profiles')
      .update({ view_only_assigned: viewOnlyAssigned, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) handleSupabaseError(error, 'updateUserViewRestriction');
  },

  adminUpdateUserPassword: async (userId: string, newPassword: string) => {
    const { error } = await supabase.rpc('admin_reset_password', {
      target_user_id: userId,
      new_password: newPassword
    });
    if (error) {
      if (error.code === '42883') {
        throw new Error("Missing SQL Function. Please run the 'admin_reset_password' SQL script.");
      }
      handleSupabaseError(error, 'adminUpdateUserPassword');
    }
  },

  deleteUser: async (userId: string) => {
    // We attempt to call the RPC. If it fails with a generic message, we'll try to provide context.
    const { error } = await supabase.rpc('delete_user', { target_user_id: userId });
    
    if (error) {
      if (error.code === '42883') {
        throw new Error("The 'delete_user' SQL function is missing in your Supabase database. Please run the SQL setup script in the Supabase Dashboard.");
      }
      handleSupabaseError(error, 'deleteUser');
    }
  },

  // -------------------------------------------------------------------------
  // Storage (Attachments)
  // -------------------------------------------------------------------------

  uploadAttachment: async (file: File): Promise<{ id: string; name: string; type: string; url: string }> => {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `${crypto.randomUUID()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(ATTACHMENTS_BUCKET)
      .upload(fileName, file, { cacheControl: '3600', upsert: false });

    if (uploadError) handleSupabaseError(uploadError, 'uploadAttachment');

    const { data: urlData } = supabase.storage
      .from(ATTACHMENTS_BUCKET)
      .getPublicUrl(fileName);

    return {
      id: crypto.randomUUID(),
      name: file.name,
      type: 'image',
      url: urlData.publicUrl
    };
  },

  deleteAttachment: async (url: string): Promise<void> => {
    try {
      const parts = url.split(`/${ATTACHMENTS_BUCKET}/`);
      if (parts.length < 2) return;
      await supabase.storage.from(ATTACHMENTS_BUCKET).remove([parts[1]]);
    } catch (e) {
      console.warn('Could not delete attachment from storage:', e);
    }
  },

  // -------------------------------------------------------------------------
  // Issues
  // -------------------------------------------------------------------------

  getIssues: async (options?: { summary?: boolean; user?: User | null }): Promise<Issue[]> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return [];

    let query = supabase
      .from('issues')
      .select(options?.summary ? ISSUE_LIST_COLUMNS : '*')
      .order('updated_at', { ascending: false });

    // Use passed user object or fetch session user profile once if needed
    const currentUser = options?.user;
    
    if (currentUser) {
      // 1. Role-based view restriction
      if (currentUser.role === UserRole.STAFF) {
        console.log("Filtering getIssues for STAFF user:", currentUser.id);
        query = (query as any).eq('reported_by', currentUser.id);
      }

      // 2. Outlet-based restriction for Managers
      if (
        currentUser.role === UserRole.MANAGER &&
        Array.isArray(currentUser.assignedOutlets) &&
        currentUser.assignedOutlets.length > 0 &&
        !currentUser.assignedOutlets.includes('__ALL__')
      ) {
        query = (query as any).in('place', currentUser.assignedOutlets);
      }
    } else {
      // Fallback: If no user object passed, we fetch profile once to ensure security
      const { data: profile } = await supabase.from('profiles').select('role, assigned_outlets').eq('id', session.user.id).maybeSingle();
      if (profile) {
        if (profile.role?.toUpperCase() === 'STAFF') {
          query = (query as any).eq('reported_by', session.user.id);
        }
        if (Array.isArray(profile.assigned_outlets) && profile.assigned_outlets.length > 0 && !profile.assigned_outlets.includes('__ALL__') && profile.role?.toUpperCase() === 'MANAGER') {
          query = (query as any).in('place', profile.assigned_outlets);
        }
      }
    }

    const { data, error } = await query;

    if (error) {
      handleSupabaseError(error, 'getIssues');
      return [];
    }
    return data.map(mapIssue);
  },

  getIssueById: async (id: string): Promise<Issue | null> => {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      handleSupabaseError(error, 'getIssueById');
      return null;
    }
    if (!data) return null;
    return mapIssue(data);
  },

  saveIssue: async (issue: Partial<Issue>): Promise<void> => {
    const dbPayload: any = { updated_at: new Date().toISOString() };

    if (issue.title !== undefined) dbPayload.title = issue.title;
    if (issue.description !== undefined) dbPayload.description = issue.description;
    if (issue.status !== undefined) dbPayload.status = issue.status;
    if (issue.priority !== undefined) dbPayload.priority = issue.priority;
    if (issue.category !== undefined) dbPayload.category = issue.category;
    if (issue.subCategory !== undefined) dbPayload.sub_category = issue.subCategory;
    if (issue.place !== undefined) dbPayload.place = issue.place;
    if (issue.location !== undefined) dbPayload.location = issue.location;
    if (issue.comments !== undefined) dbPayload.comments = issue.comments;
    if (issue.attachments !== undefined) dbPayload.attachments = issue.attachments;
    if (issue.aiAnalysis !== undefined) dbPayload.ai_analysis = issue.aiAnalysis;
    if (issue.assignedTo !== undefined) dbPayload.assigned_to = issue.assignedTo;
    if (issue.assignedToName !== undefined) dbPayload.assigned_to_name = issue.assignedToName;
    if (issue.reportedBy !== undefined) dbPayload.reported_by = issue.reportedBy;
    if (issue.reportedByName !== undefined) dbPayload.reported_by_name = issue.reportedByName;

    if (issue.id) {
      const { error } = await supabase.from('issues').update(dbPayload).eq('id', issue.id);
      if (error) handleSupabaseError(error, 'saveIssue (update)');
    } else {
      dbPayload.created_at = new Date().toISOString();
      const { error } = await supabase.from('issues').insert(dbPayload);
      if (error) handleSupabaseError(error, 'saveIssue (insert)');
    }
  },

  deleteIssue: async (id: string): Promise<void> => {
    const { error } = await supabase.from('issues').delete().eq('id', id);
    if (error) handleSupabaseError(error, 'deleteIssue');
  },

  // -------------------------------------------------------------------------
  // Realtime
  // -------------------------------------------------------------------------

  subscribeToIssues: (onChange: () => void): RealtimeChannel => {
    return supabase
      .channel('issues-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'issues' }, () => onChange())
      .subscribe();
  },

  unsubscribe: async (channel: RealtimeChannel): Promise<void> => {
    await supabase.removeChannel(channel);
  },

  generateId: (): string => crypto.randomUUID()
};
